import type { OrderStageGroup } from '@/data/orderStageGroups';
import type { OrderStageLookup } from '../../../lib/orderStages';
import { describeStatus, openStatuses, type StatusDocumentKind } from '@/domain/warehouse';

/** Display statuses each list filters on (raw aliases such as subrentals' `active` are left out). */
const FILTER_STATUSES: { readonly [kind in 'transfer' | 'purchaseOrder' | 'subrental' | 'transferRequest']: readonly string[] } = {
  transfer: [
    'draft',
    'confirmed',
    'partially_picked',
    'picked',
    'partially_shipped',
    'shipped',
    'in_transit',
    'partially_received',
    'received',
    'complete',
    'cancelled',
  ],
  purchaseOrder: ['draft', 'submitted', 'acknowledged', 'partially_received', 'received', 'closed', 'cancelled'],
  subrental: [
    'draft',
    'submitted',
    'acknowledged',
    'on_rent',
    'partially_received',
    'return_overdue',
    'received',
    'returned',
    'closed',
    'cancelled',
  ],
  transferRequest: ['open', 'awarded', 'accepted', 'fulfilled', 'rejected', 'expired', 'cancelled'],
};

export type WarehouseStatusFilterModel = {
  statuses: string[];
  defaults: string[];
  stageGroups: OrderStageGroup[];
  lookup: OrderStageLookup;
};

/** Status picker groups (Open, Done, Stopped) and labels from the shared status language. */
export function buildStatusFilterModel(
  kind: 'transfer' | 'purchaseOrder' | 'subrental' | 'transferRequest',
): WarehouseStatusFilterModel {
  const statuses = [...FILTER_STATUSES[kind]];
  const documentKind: StatusDocumentKind = kind;
  const lookup: OrderStageLookup = {};
  statuses.forEach((status, index) => {
    lookup[status] = { stageIndex: index, label: describeStatus(documentKind, status).label };
  });
  const open = kind === 'transferRequest' ? ['open'] : openStatuses(kind);
  const done = statuses.filter((status) => !open.includes(status) && describeStatus(documentKind, status).tone === 'ok');
  const stopped = statuses.filter((status) => !open.includes(status) && describeStatus(documentKind, status).tone === 'stopped');
  const rest = statuses.filter((status) => !open.includes(status) && !done.includes(status) && !stopped.includes(status));
  // Keys stay off StageFilterToolbar's event-stage display order, so the groups keep this order.
  const stageGroups: OrderStageGroup[] = [
    { group_key: 'warehouse_open', label: 'Open', sort_order: 0, statuses: [...open, ...rest] },
    { group_key: 'warehouse_done', label: 'Done', sort_order: 1, statuses: done },
    { group_key: 'warehouse_stopped', label: 'Stopped', sort_order: 2, statuses: stopped },
  ].filter((group) => group.statuses.length > 0);
  return { statuses, defaults: open, stageGroups, lookup };
}
